class QuerySwatch extends HTMLElement {
    constructor() {
        super();
        this.handles = this.querySelector("#colors-handle").innerText.trim().split(",");
        this.handlesKey = 'existing_handles';
        this.metaobjectsPairs = JSON.parse(localStorage.getItem(this.handlesKey)) || {};
    }

    /**
     * - Handle focus navigation
     * - Get new colors (if needed)
     * - Loop through the (updated) colors and build the SVG swatches
     */
    async connectedCallback() {

        this.handleFocusNavigation();
        await this.checkForNewColors();

        for (const handle in this.metaobjectsPairs) {
            this.buildSvg(this.metaobjectsPairs[handle].data.metaobject);
        }
    }


    /**
     * - Loops through the retrieved handles and checks if they already exist in the local storage object.
     *      - If they're not found then use them to create promises and fetch the colors via GraphQL requests.
     * - Once the promises are resolved (if needed), add the new colors to the existing ones and save them in localStorage
     */
    async checkForNewColors() {
        const promises = [];
        let newHandles = false;

        for (const handle of this.handles) {
            if (handle && !this.metaobjectsPairs.hasOwnProperty(handle)) {
                this.metaobjectsPairs[handle] = {};
                promises.push(this.fetchColors(handle));
                newHandles = true;
            }
        }

        if (newHandles) {
            const metaobjects = await Promise.all(promises);
            metaobjects.forEach(metaobject => {
                if (metaobject) {
                    const handle = metaobject.data.metaobject.handle;
                    this.metaobjectsPairs[handle] = metaobject;
                }
            });

            let existingHandles = JSON.parse(localStorage.getItem(this.handlesKey)) || {};

            for (const handle in this.metaobjectsPairs) {
                if (!existingHandles.hasOwnProperty(handle)) {
                    existingHandles[handle] = this.metaobjectsPairs[handle];
                }
            }

            localStorage.setItem(this.handlesKey, JSON.stringify(existingHandles));
        }
    }

    /**
     * Fetches color data from a GraphQL endpoint based on the handle.
     * @param {string} handle - The handle of the color swatch.
     * @returns {Promise<object|null>} - The fetched data or null on error.
     */
    async fetchColors(handle) {
        try {
            const response = await fetch(GRAPHQL_URL, GRAPHQL_BODY(COLOR_SWATCH_QUERY(handle)));
            return response.json();
        } catch (err) {
            console.error('Error fetching color swatch data:', err);
            return null;
        }
    }

    /**
     * Builds and inserts an SVG swatch based on retrieved color data.
     * @param {object} metaobject - The color swatch metadata.
     */
    buildSvg(metaobject) {
        const label = this.querySelector(`label[data-handle="${metaobject.handle}"]`);
        if (!label) return;

        let firstHex = null, secondHex = null, thirdHex = null;
        for (const field of metaobject.fields) {
            if (field.key === 'first_hexadecimal') firstHex = field.value;
            if (field.key === 'second_hexadecimal') secondHex = field.value;
            if (field.key === 'third_hexadecimal') thirdHex = field.value;
        }

        const svg = this.createSwatch(firstHex, secondHex, thirdHex);
        if (svg) {
            label.classList.remove('!tw-hidden');
            label.innerHTML = svg;
        }
    }

    /**
     * Generates an SVG swatch based on provided hex colors.
     * @param {string|null} firstHex - The first color hex.
     * @param {string|null} secondHex - The second color hex.
     * @param {string|null} thirdHex - The third color hex.
     * @returns {string} - The generated SVG markup.
     */
    createSwatch(firstHex, secondHex, thirdHex) {
        if (firstHex && secondHex && thirdHex) {
            return `<svg width="32" height="32" viewBox="0 0 32 32">
                <path d="M 0,0 l 16,0 A 16,16,0,0,0,-8,-14 L 0,0" transform="rotate(0 16 16) translate(16, 16)" fill="${firstHex}"></path>
                <path d="M 0,0 l 16,0 A 16,16,0,0,0,-8,-14 L 0,0" transform="rotate(120 16 16) translate(16, 16)" fill="${secondHex}"></path>
                <path d="M 0,0 l 16,0 A 16,16,0,0,0,-8,-14 L 0,0" transform="rotate(240 16 16) translate(16, 16)" fill="${thirdHex}"></path>
            </svg>`;
        }
        if (firstHex && secondHex) {
            return `<svg width="32" height="32" viewBox="0 0 32 32">
                <path d="M 0,0 l 16,0 A 16,16,0,0,0,-16,0 L 0,0" transform="rotate(0 16 16) translate(16, 16)" fill="${firstHex}"></path>
                <path d="M 0,0 l 16,0 A 16,16,0,0,0,-16,0 L 0,0" transform="rotate(180 16 16) translate(16, 16)" fill="${secondHex}"></path>
            </svg>`;
        }
        if (firstHex) {
            return `<svg width="32" height="32" viewBox="0 0 32 32">
                <circle cx="16" cy="16" r="16" fill="${firstHex}"/>
            </svg>`;
        }
        return '';
    }

    /**
     * Enables keyboard navigation for swatches using arrow keys.
     */
    handleFocusNavigation() {
        const swatches = this.querySelectorAll('input[type="radio"]');
        let currentIndex = 0;

        swatches.forEach((swatch, index) => {
            swatch.addEventListener('keydown', (event) => {
                if (event.key === 'ArrowRight') {
                    event.preventDefault();
                    currentIndex = (index + 1) % swatches.length;
                } else if (event.key === 'ArrowLeft') {
                    event.preventDefault();
                    currentIndex = (index - 1 + swatches.length) % swatches.length;
                }
                swatches[currentIndex].focus();
            });
        });
    }
}

customElements.define('query-swatch', QuerySwatch);